import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { stealthApi } from '../lib/api';
import { cn, getScoreColor, getStageColor } from '../lib/utils';
import {
  EyeOff, Loader2, Sparkles, Users, TrendingUp,
  Building2, Filter, ChevronLeft, ChevronRight, Shield
} from 'lucide-react';

const StealthPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [startups, setStartups] = useState([]);
  const [total, setTotal] = useState(0);
  const [sector, setSector] = useState('');
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const sectors = ['AI/ML', 'Fintech', 'SaaS', 'Healthcare', 'Infrastructure', 'Consumer', 'Enterprise', 'Crypto/Web3', 'Climate', 'Developer Tools'];

  const fetchStealth = async (currentPage, currentSector) => {
    setLoading(true);
    try {
      const response = await stealthApi.list({
        sector: currentSector || undefined,
        limit: LIMIT,
        skip: currentPage * LIMIT
      });
      // Deduplicate by startup_id to guard against DB duplicates
      const seen = new Set();
      const deduped = (response.data.startups || []).filter(s => {
        if (!s.startup_id || seen.has(s.startup_id)) return false;
        seen.add(s.startup_id);
        return true;
      });
      setStartups(deduped);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch stealth startups:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [sector]);

  useEffect(() => {
    fetchStealth(page, sector);
  }, [page, sector]);

  const totalPages = Math.ceil(total / LIMIT);

  const getStealthLabel = (score) => {
    if (score >= 80) return { label: 'Deep Stealth', color: 'text-rose-400 bg-rose-400/10 border-rose-400/20' };
    if (score >= 60) return { label: 'Under Radar', color: 'text-amber-400 bg-amber-400/10 border-amber-400/20' };
    if (score >= 40) return { label: 'Emerging', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20' };
    return { label: 'Semi-Public', color: 'text-zinc-400 bg-zinc-400/10 border-zinc-400/20' };
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-light font-['Outfit'] text-zinc-50">Stealth Radar</h1>
              <span className="px-2.5 py-0.5 text-xs font-mono text-rose-400 bg-rose-400/10 rounded-full border border-rose-400/20">
                ALPHA
              </span>
            </div>
            <p className="text-zinc-400 font-['Manrope']">
              <span className="text-zinc-50 font-medium">{total}</span> early-stage startups with high VC backing but low public visibility
            </p>
          </div>
        </div>

        {/* Info banner */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 mb-6 flex items-start gap-3">
          <Shield className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-zinc-300 text-sm font-['Manrope']">
              Stealth Radar identifies early-stage companies backed by top VCs with limited public presence.
              Higher stealth scores indicate companies with fewer public data points — potential alpha opportunities.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <Select
            value={sector}
            onValueChange={(v) => setSector(v === 'all' ? '' : v)}
          >
            <SelectTrigger data-testid="stealth-filter-sector" className="w-44 bg-zinc-900 border-zinc-800 text-zinc-50">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Sectors" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700 max-h-80">
              <SelectItem value="all" className="text-zinc-50">All Sectors</SelectItem>
              {sectors.map((s) => (
                <SelectItem key={s} value={s} className="text-zinc-50 hover:bg-zinc-700">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
          </div>
        ) : startups.length > 0 ? (
          <div className="grid grid-cols-2 gap-4">
            {startups.map((startup) => {
              const stealth = getStealthLabel(startup.stealth_score || 0);
              const investorScore = startup.investor_respect_score || 0;
              const teamScore = startup.team_confidence_score || 0;
              const momentumScore = startup.sector_momentum_score || 0;
              const convictionScore = startup.conviction_score || 0;

              return (
                <div
                  key={startup.startup_id}
                  onClick={() => navigate(`/startups/${startup.startup_id}`)}
                  data-testid={`stealth-card-${startup.startup_id}`}
                  className="bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer transition-all duration-200 hover:border-zinc-600 hover:bg-zinc-900/80"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-zinc-800 rounded-md flex items-center justify-center flex-shrink-0 relative">
                      <span className="text-zinc-500 font-mono text-lg">{startup.name?.charAt(0)}</span>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-rose-400/20 rounded-full flex items-center justify-center">
                        <EyeOff className="w-2.5 h-2.5 text-rose-400" />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="text-zinc-50 font-medium font-['Outfit'] truncate text-lg">{startup.name}</h3>
                        <div className="flex items-center gap-2">
                          <span className={cn("px-2 py-0.5 text-xs font-mono rounded-full border", stealth.color)}>
                            {stealth.label}
                          </span>
                          {convictionScore > 0 && (
                            <div className={cn("flex items-center gap-1 font-mono text-sm px-2 py-0.5 rounded-full",
                              convictionScore >= 80 ? 'bg-emerald-400/10 text-emerald-400' :
                              convictionScore >= 60 ? 'bg-blue-400/10 text-blue-400' :
                              'bg-amber-400/10 text-amber-400'
                            )}>
                              <Sparkles className="w-3.5 h-3.5" />
                              {Math.round(convictionScore)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      {startup.description && (
                        <p className="text-zinc-400 text-sm font-['Manrope'] mb-2 line-clamp-2">{startup.description}</p>
                      )}

                      {/* Tags */}
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        {startup.stage && (
                          <span className={cn("px-2 py-0.5 text-xs font-mono rounded-full border", getStageColor(startup.stage))}>
                            {startup.stage}
                          </span>
                        )}
                        {startup.sector && (
                          <span className="px-2 py-0.5 text-xs font-mono text-zinc-400 bg-zinc-800 rounded-full">{startup.sector}</span>
                        )}
                        {startup.source_vc && (
                          <span className="px-2 py-0.5 text-xs font-mono text-blue-400 bg-blue-400/10 rounded-full">{startup.source_vc}</span>
                        )}
                      </div>

                      {/* Scores */}
                      <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5 text-zinc-500" />
                          <span className="text-zinc-500">Investor:</span>
                          <span className={cn("font-mono", getScoreColor(investorScore))}>{Math.round(investorScore)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5 text-zinc-500" />
                          <span className="text-zinc-500">Team:</span>
                          <span className={cn("font-mono", getScoreColor(teamScore))}>{Math.round(teamScore)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <TrendingUp className="w-3.5 h-3.5 text-zinc-500" />
                          <span className="text-zinc-500">Momentum:</span>
                          <span className={cn("font-mono", getScoreColor(momentumScore))}>{Math.round(momentumScore)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-16 text-center">
            <EyeOff className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-zinc-300 font-['Outfit'] text-lg mb-2">No stealth startups found</h3>
            <p className="text-zinc-500 font-['Manrope'] text-sm">
              {sector ? 'Try a different sector filter' : 'Run sourcing to discover emerging companies'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-zinc-800">
            <p className="text-zinc-500 text-sm font-mono">
              Showing {page * LIMIT + 1}-{Math.min((page + 1) * LIMIT, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-zinc-800 text-zinc-400"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-zinc-400 text-sm font-mono px-3">Page {page + 1} of {totalPages}</span>
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="border-zinc-800 text-zinc-400"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default StealthPage;
